import React, { useId, useMemo, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports
import { Input as BaseInput } from "@base-ui/react/input";
import classNames from "classnames";
import { Scrollbars } from "react-custom-scrollbars-2";
// oxlint-disable-next-line no-restricted-imports -- Existing design-system wrapper migration debt.
import {
    GridTable,
    GridTableHeader,
    GridTableRow,
} from "@design-system/Tables/GridTable/GridTable";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { buildRangeSelection } from "./SectionedCheckboxTable.lib";
import styles from "./SectionedCheckboxTable.module.scss";

interface SectionItem<TValue> {
    value: TValue;
    searchValue?: string;
    disabled?: boolean;
    render: (props: {
        selected: boolean;
        value: TValue;
        disabled: boolean;
    }) => React.ReactNode;
}

export interface Section<TValue> {
    title: string;
    items: Array<SectionItem<TValue>>;
    allowSelectAll?: boolean;
}

interface SectionedCheckboxTableProps<TValue> {
    sections: Array<Section<TValue>>;
    value: TValue[];
    onChange: (values: TValue[]) => void;
    disabled?: boolean;
    className?: string;
    searchable?: boolean;
    autoHeightMax?: number;
}

export const SectionedCheckboxTable = <TValue extends string | number>({
    sections,
    value,
    onChange,
    disabled = false,
    className,
    searchable = false,
    autoHeightMax = 200,
}: SectionedCheckboxTableProps<TValue>) => {
    const isItemSelected = (itemValue: TValue) => value.includes(itemValue);
    const [search, setSearch] = useState("");
    const lastSelectedValueRef = useRef<TValue | null>(null);
    const idPrefix = useId();
    const sectionTitleId = (sectionIndex: number) =>
        `${idPrefix}-section-${sectionIndex}`;
    const itemTitleId = (sectionIndex: number, itemIndex: number) =>
        `${idPrefix}-item-${sectionIndex}-${itemIndex}`;

    const toggleItem = (itemValue: TValue) => {
        if (isItemSelected(itemValue)) {
            onChange(value.filter((v) => v !== itemValue));
        } else {
            onChange([...value, itemValue]);
        }
    };

    const toggleSelectAllForSection = (section: Section<TValue>) => {
        const sectionItemValues = section.items.map((item) => item.value);
        const allSectionItemsSelected = sectionItemValues.every((itemValue) =>
            value.includes(itemValue),
        );

        if (allSectionItemsSelected) {
            // Deselect all items in this section
            onChange(value.filter((v) => !sectionItemValues.includes(v)));
        } else {
            // Select all items in this section
            const newValues = [...value];
            sectionItemValues.forEach((itemValue) => {
                if (!newValues.includes(itemValue)) {
                    newValues.push(itemValue);
                }
            });
            onChange(newValues);
        }
    };

    const isAllSectionItemsSelected = (section: Section<TValue>) =>
        section.items.length > 0 &&
        section.items.every((item) => value.includes(item.value));

    const isSomeSectionItemsSelected = (section: Section<TValue>) =>
        section.items.some((item) => value.includes(item.value));

    const sectionsToDisplay = useMemo(() => {
        if (!searchable || !search) {
            return sections;
        }

        return sections.map((section) => ({
            ...section,
            items: section.items.filter((item) =>
                item.searchValue?.toLowerCase().includes(search.toLowerCase()),
            ),
        }));
    }, [sections, search, searchable]);

    // Flat, visible, selectable order used to resolve shift-click ranges
    // across section boundaries.
    const orderedSelectableValues = useMemo(
        () =>
            sectionsToDisplay.flatMap((section) =>
                section.items
                    .filter((item) => !disabled && !item.disabled)
                    .map((item) => item.value),
            ),
        [sectionsToDisplay, disabled],
    );

    const handleItemActivate = (
        e: React.MouseEvent<HTMLDivElement>,
        item: SectionItem<TValue>,
    ) => {
        // preventDefault stops the native checkbox toggle so this handler is
        // the single source of truth; propagation is intentionally left intact
        // so ancestors still receive the click.
        e.preventDefault();

        if (disabled || item.disabled) {
            return;
        }

        if (e.shiftKey && lastSelectedValueRef.current != null) {
            onChange(
                buildRangeSelection({
                    orderedValues: orderedSelectableValues,
                    anchorValue: lastSelectedValueRef.current,
                    clickedValue: item.value,
                    selected: value,
                }),
            );
        } else {
            toggleItem(item.value);
        }

        lastSelectedValueRef.current = item.value;
    };

    return (
        <GridTable className={classNames(styles.table, className)}>
            {searchable && (
                <GridTableHeader className={styles.searchRow}>
                    <div className={styles.searchField}>
                        <BaseInput
                            className={styles.searchInput}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search..."
                        />
                    </div>
                </GridTableHeader>
            )}
            <Scrollbars
                className={styles.scrollbars}
                autoHeight
                autoHeightMax={autoHeightMax}
            >
                {sectionsToDisplay.map((section, sectionIndex) => (
                    <React.Fragment key={section.title}>
                        {(section.title ||
                            section.allowSelectAll !== false) && (
                            <GridTableHeader
                                className={classNames(
                                    styles.row,
                                    styles.headerRow,
                                    {
                                        [styles.clickable]:
                                            section.allowSelectAll !== false,
                                    },
                                )}
                            >
                                <div
                                    id={sectionTitleId(sectionIndex)}
                                    onClick={() => {
                                        if (!section.allowSelectAll) {
                                            return;
                                        }
                                        toggleSelectAllForSection(section);
                                    }}
                                >
                                    {section.title}
                                </div>
                                {section.allowSelectAll !== false && (
                                    <div>
                                        <Checkbox
                                            className={styles.checkbox}
                                            checked={isAllSectionItemsSelected(
                                                section,
                                            )}
                                            indeterminate={
                                                isSomeSectionItemsSelected(
                                                    section,
                                                ) &&
                                                !isAllSectionItemsSelected(
                                                    section,
                                                )
                                            }
                                            disabled={disabled}
                                            aria-labelledby={
                                                section.title
                                                    ? sectionTitleId(
                                                          sectionIndex,
                                                      )
                                                    : undefined
                                            }
                                            onCheckedChange={() =>
                                                toggleSelectAllForSection(
                                                    section,
                                                )
                                            }
                                        />
                                    </div>
                                )}
                            </GridTableHeader>
                        )}

                        {section.items.map((item, itemIndex) => (
                            <GridTableRow
                                className={styles.row}
                                key={String(item.value)}
                            >
                                <div
                                    id={itemTitleId(sectionIndex, itemIndex)}
                                    className={styles.itemCard}
                                    onClick={(e) => handleItemActivate(e, item)}
                                >
                                    {item.render({
                                        selected: isItemSelected(item.value),
                                        value: item.value,
                                        disabled:
                                            disabled ||
                                            (item.disabled ?? false),
                                    })}
                                </div>

                                <div
                                    onClickCapture={(e) => {
                                        if (e.shiftKey) {
                                            handleItemActivate(e, item);
                                        }
                                    }}
                                    onClick={(e) => {
                                        if (!e.shiftKey) {
                                            handleItemActivate(e, item);
                                        }
                                    }}
                                >
                                    <Checkbox
                                        className={styles.checkbox}
                                        checked={isItemSelected(item.value)}
                                        disabled={disabled || item.disabled}
                                        aria-labelledby={itemTitleId(
                                            sectionIndex,
                                            itemIndex,
                                        )}
                                    />
                                </div>
                            </GridTableRow>
                        ))}
                    </React.Fragment>
                ))}
            </Scrollbars>
        </GridTable>
    );
};
